import { Injectable, inject } from "@angular/core";
import { isNil } from "lodash";
import { Observable, Subject, merge } from "rxjs";
import {
    filter,
    map,
    scan,
    shareReplay,
    switchMap,
    tap,
    withLatestFrom
} from "rxjs/operators";
import { UpdateVirkeskopskontraktLeverantorCommand } from "../commands/betalning/UpdateVirkeskopskontraktLeverantorCommand";
import { IVKKontraktLeverantor } from "../dto/kontrakt/kontrakt-betalningar-dto";
import { AvtalService } from "./avtal.service";
import { KontraktCommandService } from "./kontrakt-command.service";
import { KontraktMediatorService } from "./kontrakt-mediator.service";
import { LeverantorMediatorService } from "./leverantor-mediator.service";

@Injectable()
export class VirkeskopskontraktLeverantorMediatorService {
    readonly kontraktLeverantorer$: Observable<IVKKontraktLeverantor[]>;
    readonly setForskottSrc = new Subject<IVKKontraktLeverantor>();
    readonly setMakeGodkannandeKravsEjSrc = new Subject<IVKKontraktLeverantor[]>();

    private readonly avtalService = inject(AvtalService);
    private readonly kontraktCommandService = inject(KontraktCommandService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly leverantorMediatorService = inject(LeverantorMediatorService);

    constructor() {
        const reloadKontraktLeverantorer$ =  this.kontraktMediatorService.kontrakt$
            .pipe(
                filter(kontrakt => !isNil(kontrakt.id)),
                switchMap(kontrakt => this.avtalService.getKontraktLeverantorer(kontrakt.id)),
                shareReplay(1),
                map(forskott => (_: IVKKontraktLeverantor[]) => forskott));

        const leverantorConnected$ = this.leverantorMediatorService.leverantorChange$.pipe(
            filter(lev => lev.changeType === "add"),
            withLatestFrom(this.kontraktMediatorService.kontrakt$),
            map(([
                lev,
                kontrakt
            ]) => ({
                kontraktGuid: kontrakt.guid,
                leverantorGuid: lev.leverantor.guid,
                forskott: false
            } as IVKKontraktLeverantor)),
            tap(added => this.updateKontraktLeverantorCommand(added)),
            map(added => (vklev: IVKKontraktLeverantor[]) => [
                ...vklev,
                added
            ])
        );

        const setForskott$ = this.setForskottSrc.pipe(
            map((kontraktLeverantor) => (current: IVKKontraktLeverantor[]) => {
                const index = current.findIndex(f => f.leverantorGuid === kontraktLeverantor.leverantorGuid);
                if(index > -1) {
                    const updated: IVKKontraktLeverantor = {
                        ...current[index],
                        forskott: kontraktLeverantor.forskott,
                        betalplan: kontraktLeverantor.betalplan
                    };
                    this.updateKontraktLeverantorCommand(updated);
                    return [
                        ...current.slice(0, index),
                        updated,
                        ...current.slice(index + 1)
                    ];
                }
                return [
                    ...current,
                    kontraktLeverantor
                ];
            })
        );

        const setMakeGodkannandeKravsEj$ = this.setMakeGodkannandeKravsEjSrc.pipe(
            map((forskottList) => (current: IVKKontraktLeverantor[]) => current.map(f => {
                const makeGodkannandeKravsEj = forskottList
                // eslint-disable-next-line sonarjs/no-nested-functions
                    .some(x => x.leverantorGuid === f.leverantorGuid);
                const updated = {
                    ...f,
                    makeGodkannandeKravsEj: makeGodkannandeKravsEj
                };
                if(f.makeGodkannandeKravsEj !== makeGodkannandeKravsEj) {
                    this.updateKontraktLeverantorCommand(updated);
                }
                return updated;
            }))
        );

        this.kontraktLeverantorer$ = merge(
            reloadKontraktLeverantorer$,
            setForskott$,
            setMakeGodkannandeKravsEj$,
            leverantorConnected$).pipe(
            scan((
                acc: IVKKontraktLeverantor[],
                mutatorFn: (forskott: IVKKontraktLeverantor[]) => IVKKontraktLeverantor[]) => mutatorFn(acc), [])
        );
    }

    private updateKontraktLeverantorCommand(kontraktLeverantor: IVKKontraktLeverantor) {
        const command = new UpdateVirkeskopskontraktLeverantorCommand(kontraktLeverantor);
        this.kontraktCommandService.update(command);
    }
}
